// For PDF export with chart images

import {
  Component,
  OnInit,
  OnDestroy,
  ViewChildren,
  QueryList,
  ElementRef,
} from "@angular/core";
import { Color, LegendPosition, ScaleType } from "@swimlane/ngx-charts";
import ExcelJS from "exceljs";
import pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";

(pdfMake as any).vfs = pdfFonts.vfs;

import {
  forkJoin,
  catchError,
  of,
  Subscription,
  fromEvent,
  debounceTime,
} from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import { BreakdownItem } from "../../../models/reportItem.model";
import { NavigationService } from "../../../services/navigation.service";
import { DonorWithMetrics, ReportingService } from "../../../services/reporting.service";



export interface ChartDataItem {
  name: string;
  value: number;
}

export interface ChartSettings {
  legendPosition: LegendPosition;
  arcWidth: number;
  trimLabels: boolean;
  view: [number, number];
}

@Component({
  selector: "app-reporting",
  templateUrl: "./reporting.component.html",
  styleUrl: "./reporting.component.scss",
})
export class ReportingComponent implements OnInit, OnDestroy {
  @ViewChildren("chartContainer") chartContainers!: QueryList<ElementRef>;
  bloodTypeBreakdown: BreakdownItem[] = [];
  sexBreakdown: BreakdownItem[] = [];
  ethnicityBreakdown: BreakdownItem[] = [];
  eligibilityBreakdown: BreakdownItem[] = [];
  metricsBreakdown: Record<string, BreakdownItem[]> = {};

  chartOptionsEligibility: ChartDataItem[] = [];
  chartOptionsMetrics: Record<string, ChartDataItem[]> = {};

  chartEntries: { key: string; data: ChartDataItem[] }[] = [];

  chartSettings: ChartSettings = {
    legendPosition: LegendPosition.Right,
    arcWidth: 0.5,
    trimLabels: false,
    view: [400, 300] as [number, number],
  };

  responsiveChartView: [number, number] = [500, 400];
  responsiveSmallChartView: [number, number] = [400, 300];
  resizeSubscription!: Subscription;

  filteredChartEntries: { key: string; data: ChartDataItem[] }[] = [];
  filteredEligibilityChart: ChartDataItem[] = [];

  filterFromDate: string | null = null;
  filterToDate: string | null = null;
  activeCardFilter: string | null = null;

  searchQuery = "";
  donors: any[] = [];
  filteredDonors: DonorWithMetrics[] = []; // Holds the raw data for export, typed correctly

  availableFilters = [
    // {
    //   key: 'donors',
    //   label: 'Donors',
    //   options: [''],
    // },
    {
      key: "eligibilityStatus",
      label: "Eligibility",
      options: [""],
    },
    {
      key: "age",
      label: "Age",
      options: [""],
    },
    {
      key: "bloodType",
      label: "Blood Type",
      options: [""],
    },
    {
      key: "bmi",
      label: "BMI",
      options: [""],
    },
    {
      key: "gender",
      label: "Gender",
      options: [""],
    },
    {
      key: "relationship",
      label: "Relationship",
      options: [""],
    },
    {
      key: "recipients",
      label: "Recipient Name",
      options: [""],
    },
    // {
    //   key: "assignedTo",
    //   label: "Assigned To",
    //   options: [""],
    // },
  ];

  activeSubFilter: string | null = null;
  activeFilters: Record<string, string[]> = {};
  filterSearch = "";
  subFilterTopOffset = 0;

  filters: Record<string, Set<string>> = {};

  chartLabels: Record<string, string> = {
    age: "Age",
    bmi: "BMI",
    bsa: "BSA",
    bloodtype: "Blood Type",
    relationship: "Relationship",
    gender: "Gender",
    elgibility: "Eligibility",
    recipients: "Recipient Name",
    status: "Status",
    assignedTo: "Assigned To",
  };

  colorScheme: Color = {
    name: "customScheme",
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ["#1f77b4", "#ff7f0e", "#2ca02c", "#9467bd", "#8c564b", "#e377c2"],
  };

  loading = false;
  hasData = false;

  constructor(
    private reportingService: ReportingService,
    private navigationService: NavigationService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    const today = new Date();

    const currentMonth = today.getMonth();
    const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
    const startOfQuarter = new Date(today.getFullYear(), quarterStartMonth, 1);

    this.filterFromDate = startOfQuarter.toISOString().substring(0, 10); // 'YYYY-MM-DD'
    this.filterToDate = today.toISOString().substring(0, 10);

    this.setResponsiveChartSizes();

    this.resizeSubscription = fromEvent(window, "resize")
      .pipe(debounceTime(200))
      .subscribe(() => {
        this.setResponsiveChartSizes();
      });

    this.loadChartData();
  }

  ngOnDestroy(): void {
    this.resizeSubscription?.unsubscribe();
  }

  setResponsiveChartSizes() {
    const width = window.innerWidth;

    if (width <= 1024) {
      this.responsiveChartView = [350, 280];
      this.responsiveSmallChartView = [350, 280];
    } else if (width <= 1650) {
      this.responsiveChartView = [400, 320];
      this.responsiveSmallChartView = [360, 280];
    } else {
      this.responsiveChartView = [500, 400];
      this.responsiveSmallChartView = [400, 300];
    }
  }

  exportDataAsExcel() {
    const rawDataForExport = this.filteredDonors;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Raw Report Data");

    // Add header section
    const filteredDateRangeRow = worksheet.addRow(["Data Date Range:"]);
    filteredDateRangeRow.font = { bold: true };
    worksheet.addRow([
      `From ${this.filterFromDate || "N/A"} to ${this.filterToDate || "N/A"}`,
    ]);
    worksheet.addRow([]);

    if (rawDataForExport && rawDataForExport.length > 0) {
      // Sort rawDataForExport by completedAt in ascending order (oldest first)
      rawDataForExport.sort(
        (a, b) =>
          new Date(a["completedAt"]).getTime() -
          new Date(b["completedAt"]).getTime()
      );
      // Only include columns defined in columnLabelMap, in order
      const columnLabelMap: Record<string, string> = {
        completedAt: "Questionnaire Date",
        userId: "Donor Email",
        fullName: "Full Name",
        Age: "Age",
        BMI: "BMI",
        BSA: "BSA",
        BloodType: "Blood Type",
        Gender: "Gender",
        Ethnicity: "Ethnicity",
        EligibilityStatus: "Eligibility Status",
        RecipientName: "Recipient Name",
        RecipientAge: "Recipient Age",
        Relationship: "Relationship",
        DonorMotivationLevel: "Donor Motivation Level",
      };

      const manualOrderKeys = Object.keys(columnLabelMap);
      const displayHeaders = manualOrderKeys.map((h) => columnLabelMap[h] || h);
      const headerRow = worksheet.addRow(displayHeaders);
      headerRow.font = { bold: true };

      rawDataForExport.forEach((item) => {
        // Only include keys in manualOrderKeys, get from top-level or metrics (metrics: lowercase)
        const row: any[] = manualOrderKeys.map((header) => {
          const metricKey = header;
          // console.log("key: ", metricKey);
          const val = item[header] ?? item["metrics"]?.[metricKey];
          if (val === undefined || val === null) return "";
          if (["BMI", "BSA", "Age", "RecipientAge"].includes(header)) {
            const num = parseFloat(val);
            return isNaN(num) ? "" : num;
          }
          return val;
        });
        worksheet.addRow(row);
      });

      worksheet.columns.forEach((column) => {
        if (!column || typeof column.eachCell !== "function") return;

        let maxLength = 10;
        column.eachCell({ includeEmpty: true }, (cell) => {
          maxLength = Math.max(
            maxLength,
            cell.value ? String(cell.value).length : 0
          );
        });
        column.width = maxLength + 2;
      });
    } else {
      worksheet.addRow([
        "No raw data available for the selected date range and filters.",
      ]);
    }

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const getFormattedDateString = (date: Date): string => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const formatDateForFilename = (
        dateInput: Date | string | null | undefined,
        defaultName: string
      ): string => {
        if (!dateInput) return defaultName;
        const dateObj =
          dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (isNaN(dateObj.getTime())) return defaultName;
        return getFormattedDateString(dateObj);
      };

      const fromDatePart = formatDateForFilename(
        this.filterFromDate,
        "StartDate"
      );
      const toDatePart = formatDateForFilename(this.filterToDate, "EndDate");
      const timestampPart = Math.floor(new Date().getTime() / 1000).toString();
      const dynamicFilename = `rawDonorData_${fromDatePart}_${toDatePart}_${timestampPart}.xlsx`;

      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = dynamicFilename;
      a.click();
    });
  }

  exportDataAsPDF(): void {
    // Find all SVG elements inside chart containers
    const chartElements =
      this.chartContainers
        ?.toArray()
        .map((ref) => ref.nativeElement.querySelector("svg"))
        .filter(Boolean) || [];
    // Helper: Convert SVG element to PNG data URL with improved padding and dimension handling (fix label clipping)
    const convertSvgToImage = (svgEl: SVGSVGElement): Promise<string> => {
      return new Promise((resolve, reject) => {
        const clonedSvg = svgEl.cloneNode(true) as SVGSVGElement;
        clonedSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        clonedSvg.setAttribute("overflow", "visible");

        const bbox = svgEl.getBBox(); // Get bounding box of content
        const padding = 60;
        const fontSize = 16;

        const viewBox = [
          bbox.x - padding,
          bbox.y - padding,
          bbox.width + 2 * padding,
          bbox.height + 2 * padding,
        ].join(" ");

        clonedSvg.setAttribute("viewBox", viewBox);
        clonedSvg.setAttribute("width", (bbox.width + 2 * padding).toString());
        // Increase height to accommodate subheader
        clonedSvg.setAttribute(
          "height",
          (bbox.height + 2 * padding + fontSize + 10).toString()
        );

        const svgData = new XMLSerializer().serializeToString(clonedSvg);
        const svgBlob = new Blob([svgData], {
          type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(svgBlob);

        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = bbox.width + 2 * padding;
          // Add space for subheader above the image
          canvas.height = bbox.height + 2 * padding + fontSize + 10;

          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            // Draw subheader text above the image
            ctx.font = `${fontSize}px Arial`;
            ctx.fillStyle = "#000000";
            ctx.textAlign = "center";

            let subheader = "";
            // Try to find the chart label from filteredChartEntries or eligibility
            if (svgEl === chartElements[0]) {
              subheader = "Eligibility Status";
            } else {
              const idx = chartElements.indexOf(svgEl) - 1;
              if (idx >= 0 && this.filteredChartEntries[idx]) {
                subheader = this.getChartLabel(
                  this.filteredChartEntries[idx].key
                );
              }
            }
            ctx.textBaseline = "top";
            ctx.fillText(subheader, canvas.width / 2, 0);
            // Draw the image below the subheader
            ctx.drawImage(img, 0, fontSize + 4);
          }

          URL.revokeObjectURL(url);
          resolve(canvas.toDataURL("image/png"));
        };

        img.onerror = reject;
        img.src = url;
      });
    };

    const docDefinition: any = {
      content: [
        { text: "Donor Report Summary", style: "header" },
        {
          text: `Date Range: ${this.filterFromDate} to ${this.filterToDate}`,
          margin: [0, 0, 0, 10],
        },
        { text: "Charts Overview", style: "header", margin: [0, 0, 0, 30] },
      ],
      styles: {
        header: {
          fontSize: 16,
          bold: true,
          margin: [0, 0, 0, 10],
        },
      },
    };

    Promise.all(chartElements.map((el) => convertSvgToImage(el))).then(
      (images) => {
        // Eligibility chart is the first image (images[0])
        // The rest of the images correspond to filteredChartEntries
        // Add eligibility chart if present
        if (images.length > 0) {
          const eligibilityImage = images[0];
          docDefinition.content.push({
            image: eligibilityImage,
            width: 300,
            fit: [280, 220],
            alignment: "center",
            margin: [0, 0, 0, 12],
          });
        }

        const largeCharts = ["age", "bloodtype", "gender"];
        // Add all metric charts directly, no batching
        for (let i = 0; i < this.filteredChartEntries.length; i++) {
          // images[0] is eligibility, so shift by +1
          const img = images[i + 1];
          const entry = this.filteredChartEntries[i];
          if (entry && img) {
            const chartImage = largeCharts.includes(entry.key)
              ? {
                  image: img,
                  width: 300,
                  fit: [280, 220],
                  alignment: "center",
                  margin: [0, 0, 0, 12],
                }
              : {
                  image: img,
                  width: 400,
                  fit: [380, 300],
                  alignment: "center",
                  margin: [0, 0, 0, 16],
                };
            docDefinition.content.push(chartImage);
          }
        }

        // Insert the donor table after charts
        docDefinition.content.push({ text: "", pageBreak: "after" });
        docDefinition.content.push({
          text: "Donor Table",
          style: "header",
          margin: [0, 0, 0, 10],
        });

        // Horizontally-oriented donor table: metrics as columns, values as a single row
        if (this.filteredDonors && this.filteredDonors.length > 0) {
          // Use first donor as example for keys/columns
          const profileData = {
            "Full Name": this.filteredDonors[0]["fullName"] || "",
            "Eligibility": this.filteredDonors[0]["metrics"]?.EligibilityStatus || "",
            "Age": this.filteredDonors[0]["metrics"]?.Age || "",
            "Blood Type": this.filteredDonors[0]["metrics"]?.BloodType || "",
            "Gender": this.filteredDonors[0]["metrics"]?.Gender || "",
            "BMI": this.filteredDonors[0]["metrics"]?.BMI || "",
            "Relationship": this.filteredDonors[0]["metrics"]?.Relationship || "",
            "Recipient Name": this.filteredDonors[0]["metrics"]?.RecipientName || "",
          };
          const keys = Object.keys(profileData);
          // Instead of just one donor, show all donors as rows
          const body = [
            keys,
            ...this.filteredDonors.map((d) =>
              keys.map((key) => {
                switch (key) {
                  case "Full Name":
                    return d["fullName"] || "";
                  case "Eligibility":
                    return d["metrics"]?.EligibilityStatus || "";
                  case "Age":
                    return d["metrics"]?.Age || "";
                  case "Blood Type":
                    return d["metrics"]?.BloodType || "";
                  case "Gender":
                    return d["metrics"]?.Gender || "";
                  case "BMI":
                    return d["metrics"]?.BMI || "";
                  case "Relationship":
                    return d["metrics"]?.Relationship || "";
                  case "Recipient Name":
                    return d["metrics"]?.RecipientName || "";
                  default:
                    return "";
                }
              })
            ),
          ];
          docDefinition.content.push({
            style: 'tableExample',
            table: {
              headerRows: 1,
              body,
            },
            layout: 'lightHorizontalLines',
            margin: [0, 20, 0, 0],
          });
        }

        pdfMake.createPdf(docDefinition).download("donor-report.pdf");
      }
    );

    if (chartElements.length === 0) {
      pdfMake.createPdf(docDefinition).download("donor-report.pdf");
      return;
    }
  }

  loadChartData() {
    this.loading = true;
    // Set dateTo to end of the day (23:59:59.999)
    const fromDate = new Date(this.filterFromDate!);
    const toDate = new Date(this.filterToDate!);
    toDate.setHours(23, 59, 59, 999);
    const filters = {
      dateFrom: fromDate.toISOString(),
      dateTo: toDate.toISOString(),
      status: "Completed",
    };

    forkJoin({
      eligibility: this.reportingService.getEligibilityBreakdown(filters).pipe(
        catchError((error) => {
          console.error("Error loading eligibility breakdown", error);
          return of([]);
        })
      ),
      donorsRawData: this.reportingService.getDonorsWithMetrics(filters).pipe(
        catchError((error) => {
          console.error("Error loading raw donor data for export", error);
          return of([]); // Return empty array on error
        })
      ),
      metrics: this.reportingService
        .getMetricsBreakdown({
          metricKeys:
            "age,bloodtype,bmi,relationship,gender,recipientname",
          dateFrom: fromDate.toISOString(),
          dateTo: toDate.toISOString(),
          status: "Completed",
        })
        .pipe(
          catchError((error) => {
            console.error("Error loading metrics breakdown", error);
            return of({});
          })
        ),
    }).subscribe(({ eligibility, metrics, donorsRawData }) => {
      this.eligibilityBreakdown = eligibility;
      this.chartOptionsEligibility = this.getChartData(eligibility);

      this.filteredEligibilityChart = this.getFilteredEligibilityChartData();

      const eligibilityOptions = eligibility
        .map((item) => item.label)
        .sort((a, b) => a.localeCompare(b));
      const eligibilityFilter = this.availableFilters.find(
        (f) => f.key === "eligibilityStatus"
      );
      if (eligibilityFilter) {
        eligibilityFilter.options = eligibilityOptions;
      }

      this.metricsBreakdown = metrics;

      const desiredOrder = [
        "age",
        "bloodtype",
        "gender",
        "bmi",
        "relationship",
        "recipients",
      ];

      const metricsMapTyped = metrics as Record<string, BreakdownItem[]>;

      this.chartEntries = desiredOrder
        .filter((key) => metricsMapTyped[key]) // only include keys that exist in metrics
        .map((key) => ({
          key,
          data: this.getChartData(metricsMapTyped[key]),
        }));

      this.filteredChartEntries = this.chartEntries;

      // Assuming 'eligibility' contains or can represent the raw filtered donors for export
      this.filteredDonors = donorsRawData ? [...donorsRawData] : []; // Use the new data source

      for (const [key, data] of Object.entries(metrics)) {
        const filter = this.availableFilters.find(
          (f) => f.key.toLowerCase() === key.toLowerCase()
        );
        if (filter) {
          filter.options = data
            .map((item) => item.label)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
        }
      }

      this.hasData =
        this.chartOptionsEligibility.length > 0 ||
        this.chartEntries.some((entry) => entry.data.length > 0);

      this.loading = false;
    });
  }

  hasAnyFiltersApplied(): boolean {
    return Object.values(this.activeFilters).some(
      (options) => Array.isArray(options) && options.length > 0
    );
  }

  getChartLabel(key: string): string {
    return this.chartLabels[key.toLowerCase()] || key;
  }

  getMetricOptions(metricKey: string): string[] {
    return Array.from(
      new Set(
        this.donors
          .map(
            (d) =>
              d.metrics?.find((m: any) => m.metric_key === metricKey)
                ?.metric_value
          )
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }

  getChartTotal(data: { name: string; value: number }[]): number {
    return data.reduce((sum, item) => sum + item.value, 0);
  }

  resetFilter(filterKey: string): void {
    // Clear selected options for this filter
    if (this.filters[filterKey]) {
      this.filters[filterKey].clear();
    }

    // Clear active filters for this key
    this.activeFilters[filterKey] = [];

    // Reapply filters to update charts
    this.applyChartFilters();
  }

  onDropdownClose(isOpen: boolean) {
    if (!isOpen) {
      this.activeSubFilter = null;
    }
  }

  onSliceClick(metricKey: string, event: any) {
    const label = event.name;

    const filtersToApply = { ...this.activeFilters };

    // Add clicked slice to filters if it's not already there
    if (!filtersToApply[metricKey]) {
      filtersToApply[metricKey] = [];
    }
    if (!filtersToApply[metricKey].includes(label)) {
      filtersToApply[metricKey].push(label);
    }

    this.navigationService.state = {
      appliedFilters: filtersToApply,
      fromSection: metricKey,
      selectedLabel: label,
    };

    // Navigate to donor overview and pass filters in query params or state
    this.router.navigate(["/hospital/admin/dashboard/donoroverview"]);
  }

  clearFilters(): void {
    // Clear internal filter tracking
    Object.keys(this.activeFilters).forEach((key) => {
      this.activeFilters[key] = [];
    });

    // Reset chart entries to show full (unfiltered) data
    this.filteredChartEntries = [...this.chartEntries];
    this.filteredEligibilityChart = [...this.chartOptionsEligibility];

    // Reset UI filter state
    this.activeSubFilter = null;
    this.activeCardFilter = null;
  }

  // searchDonors() {
  //   this.filteredDonors = this.donors.filter(
  //     (donor) =>
  //       `${donor.firstName.trim()} ${donor.lastName.trim()}`
  //         .toLowerCase()
  //         .trim()
  //         .includes(this.searchQuery.toLowerCase().trim()) ||
  //       donor.firstName
  //         ?.toLowerCase()
  //         .includes(this.searchQuery.toLowerCase()) ||
  //       donor.lastName
  //         ?.toLowerCase()
  //         .includes(this.searchQuery.toLowerCase()) ||
  //       donor.email?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
  //       donor.assignedTo
  //         ?.toLowerCase()
  //         .includes(this.searchQuery.toLowerCase()) ||
  //       this.getRecipientNameMetric(donor.metrics)
  //         ?.toLowerCase()
  //         .includes(this.searchQuery.toLowerCase())
  //   );
  // }

  // getRecipientNameMetric(metrics: any[]): string {
  //   if (!Array.isArray(metrics)) return "";

  //   const nameMetric = metrics.find((m) => m.metric_key === "RecipientName");
  //   const fullName = nameMetric?.metric_value?.trim() || "";

  //   return fullName;
  // }

  getFilteredFilters(): any[] {
    if (!this.filterSearch) return this.availableFilters;
    const lowerSearch = this.filterSearch.toLowerCase();
    return this.availableFilters.filter((f) =>
      f.label.toLowerCase().includes(lowerSearch)
    );
  }

  getFilterLabel(key: string): string {
    return this.availableFilters.find((f) => f.key === key)?.label || "";
  }

  getSubFilterOptions(key: string): string[] {
    return this.availableFilters.find((f) => f.key === key)?.options ?? [];
  }

  hasSelectedFilterOption(filterKey: string): boolean {
    return (
      this.activeFilters[filterKey] &&
      Array.isArray(this.activeFilters[filterKey]) &&
      this.activeFilters[filterKey].length > 0
    );
  }

  setActiveSubFilter(filterKey: string, elementRef: HTMLElement) {
    this.activeSubFilter = filterKey;
    const parentTop =
      (
        elementRef.closest(".filter-dropdown-menu") as HTMLElement
      )?.getBoundingClientRect().top ?? 0;
    const itemTop = elementRef.getBoundingClientRect().top;

    this.subFilterTopOffset = itemTop - parentTop + 46;
  }

  isSelectedFilterOption(filterKey: string, option: string): boolean {
    return this.activeFilters[filterKey]?.includes(option);
  }

  toggleFilterOption(metricKey: string, optionLabel: string): void {
    // const filter = this.filters[metricKey];

    if (!this.activeFilters[metricKey]) {
      this.activeFilters[metricKey] = [];
    }

    const index = this.activeFilters[metricKey].indexOf(optionLabel);
    if (index > -1) {
      this.activeFilters[metricKey].splice(index, 1);
    } else {
      this.activeFilters[metricKey].push(optionLabel);
    }

    // this.applyChartFilters();
  }

  applyChartFilters(): void {
    const userSetsByFilter: Set<string>[] = [];

    for (const metricKey of Object.keys(this.activeFilters)) {
      const selectedLabels = this.activeFilters[metricKey] || [];

      if (selectedLabels.length === 0) continue;

      const userIds: string[] = [];

      if (metricKey === "eligibilityStatus") {
        for (const label of selectedLabels) {
          const match = this.eligibilityBreakdown.find(
            (b) => b.label === label
          );
          if (match?.userIds) {
            userIds.push(...match.userIds);
          }
        }
      } else {
        const breakdown = this.metricsBreakdown[metricKey.toLowerCase()] || [];

        for (const label of selectedLabels) {
          const match = breakdown.find((b) => b.label === label);
          if (match?.userIds) {
            userIds.push(...match.userIds);
          }
        }
      }

      userSetsByFilter.push(new Set(userIds));
    }

    // ✅ Step 2: Intersect all user ID sets
    let selectedUserIds: Set<string>;

    if (userSetsByFilter.length === 0) {
      selectedUserIds = new Set(); // no filters applied
    } else {
      selectedUserIds = userSetsByFilter.reduce((a, b) => {
        return new Set([...a].filter((x) => b.has(x)));
      });
    }

    // If no filters applied, show all original chart data
    if (userSetsByFilter.length === 0) {
      // No filters applied – show original data
      this.filteredChartEntries = [...this.chartEntries];
      this.filteredEligibilityChart = this.chartOptionsEligibility;
      this.activeSubFilter = null;
      return;
    }

    if (selectedUserIds.size === 0) {
      // Filters applied, but no matching users – show empty state
      this.filteredChartEntries = [];

      this.filteredEligibilityChart = [];

      this.activeSubFilter = null;
      return;
    }

    // Filter each chart entry to keep only slices for matched users
    this.filteredChartEntries = this.chartEntries.map((entry) => {
      const original = this.metricsBreakdown[entry.key.toLowerCase()] || [];

      const filteredData = original
        .map((slice) => {
          const userIds = slice.userIds || [];
          const matched = userIds.filter((uid) => selectedUserIds.has(uid));
          return {
            name: slice.label,
            value: matched.length,
          };
        })
        .filter((slice) => slice.value > 0);

      return {
        key: entry.key,
        data: filteredData,
      };
    });

    // Also filter eligibility chart the same way
    this.filteredEligibilityChart = this.eligibilityBreakdown
      .map((item) => {
        const matched =
          item.userIds?.filter((uid) => selectedUserIds.has(uid)) ?? [];
        return {
          name: item.label,
          value: matched.length,
        };
      })
      .filter((item) => item.value > 0);

    this.activeSubFilter = null;
  }

  getChartData(breakdown: BreakdownItem[]): ChartDataItem[] {
    return breakdown.map((item) => {
      let label = item.label;

      // Standardize BMI bucket labels for chart display
      if (label.toLowerCase().includes("underweight")) {
        label = "BMI < 18.5";
      } else if (label.toLowerCase().includes("normal")) {
        label = "18.5  BMI < 25";
      } else if (label.toLowerCase().includes("overweight")) {
        label = "25 ≤ BMI < 32";
      } else if (label.toLowerCase().includes("obese")) {
        label = "BMI ≥ 32";
      }

      return {
        name: label,
        value: item.count,
      };
    });
  }

  getFilteredEligibilityChartData(): ChartDataItem[] {
    const selectedUserIds = new Set<string>();

    // Step 1: collect all userIds from activeFilters (eligibility + metrics)
    for (const metricKey of Object.keys(this.activeFilters)) {
      const selectedLabels = this.activeFilters[metricKey] || [];

      if (metricKey === "elgibility") {
        for (const label of selectedLabels) {
          const match = this.eligibilityBreakdown.find(
            (b) => b.label === label
          );
          if (match?.userIds) {
            match.userIds.forEach((uid) => selectedUserIds.add(uid));
          }
        }
      } else {
        const breakdown = this.metricsBreakdown[metricKey.toLowerCase()] || [];
        for (const label of selectedLabels) {
          const match = breakdown.find((b) => b.label === label);
          if (match?.userIds) {
            match.userIds.forEach((uid) => selectedUserIds.add(uid));
          }
        }
      }
    }

    // Step 2: filter eligibility chart data based on selectedUserIds
    if (selectedUserIds.size === 0) {
      return this.chartOptionsEligibility; // unfiltered
    }

    return this.eligibilityBreakdown
      .map((item) => {
        const count =
          item.userIds?.filter((uid) => selectedUserIds.has(uid)).length || 0;

        return { name: item.label, value: count };
      })
      .filter((entry) => entry.value > 0);
  }

  hasFilteredResults(): boolean {
    const hasEligibilityData = this.filteredEligibilityChart.length > 0;
    const hasMetricData = this.filteredChartEntries.some(
      (e) => e.data.length > 0
    );
    return hasEligibilityData || hasMetricData;
  }

  formatEligbilityLabel = (label: string): string => {
    const entry = this.filteredEligibilityChart.find((e) => e.name === label);
    if (!entry) return label;

    // return label;

    return `${label} - Count: ${entry?.value}`;
  };

  formatLabel = (label: string): string => {
    const entry = this.filteredChartEntries.find((e) =>
      e.data.some((d) => d.name === label)
    );

    if (!entry) return label;

    const value = entry.data.find((d) => d.name === label)?.value || 0;

    // return label;

    return `${label}`;
  };

  onDateChange(): void {
    if (this.filterFromDate && this.filterToDate) {
      this.loadChartData();
    }
  }

  // getAgeBucket(age: string): string {
  //   const n = parseInt(age, 10);
  //   if (isNaN(n)) return "";
  //   if (n < 20) return "Under 20";
  //   if (n < 30) return "20-29";
  //   if (n < 40) return "30-39";
  //   if (n < 50) return "40-49";
  //   if (n < 60) return "50-59";
  //   if (n < 70) return "60-69";
  //   return "70+";
  // }

  // getBMIBucket(bmi: string): string {
  //   const n = parseFloat(bmi);
  //   if (isNaN(n)) return "";
  //   if (n < 18.5) return "Underweight";
  //   if (n < 25) return "Normal";
  //   if (n < 30) return "Overweight";
  //   return "Obese";
  // }

  // getBSABucket(bsa: string): string {
  //   const n = parseFloat(bsa);
  //   if (isNaN(n)) return "";
  //   if (n < 1.5) return "<1.5";
  //   if (n < 1.7) return "1.5–1.7";
  //   if (n < 1.9) return "1.7–1.9";
  //   return "1.9+";
  // }
}
